import clsx from 'clsx';
import { it } from 'date-fns/locale/it';
import React, { useState } from 'react';
import DatePicker, { CalendarContainer, registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { colors } from '../../colors.ts';
import { CalendarIcon } from '../../icons/calendarIcon.tsx';
import { ClockIcon } from '../../icons/clockIcon.tsx';
import { ChevronIcon } from '../../icons/pagesIcon.tsx';
import './style.css';

registerLocale('it', it);

interface Props {
    startDate: Date | null;
    setStartDate: (date: Date | null) => void;
    label?: string;
    error?: boolean;
    disabled?: boolean;
    supportingText?: string
    type: 'date' | 'time'
    timeIntervals?: number
    includeTimes?: Date[]
    markedDates?: Date[]
    onMonthChange?: (month: number) => void
    onYearChange?: (year: number) => void
}

export function Calendar(props: Props) {
    const { startDate, setStartDate, label, error, disabled, supportingText, type, timeIntervals, includeTimes, markedDates } = props;
    const [open, setOpen] = useState(false);
    const customLocale = {
        ...it,
        options: {
            ...it.options,
            weekStartsOn: 1 as const,
        },
        localize: {
            ...it.localize,
            day: (n: number) => ['D', 'L', 'M', 'M', 'G', 'V', 'S'][n]
        }
    };

    registerLocale('it-custom', customLocale);
    //@ts-ignore
    const MyContainer = ({ className, children }) => (
        <CalendarContainer className={className + ' bg-white border border-neutral-100 p-3 shadow-sm gap-1 flex rounded-lg'}>
            <div style={{ position: "relative", backgroundColor: 'white' }}>{children}</div>
        </CalendarContainer>
    )

    return (
        <div className='flex flex-col items-start gap-2 w-full'>
            <span className='text-label-sm text-neutral-700 font-semibold'>{label}</span>
            <div className={clsx('h-10 flex flex-row-reverse border rounded-lg px-3 py-[10px] w-full', {
                "bg-white": !disabled,
                'border-red-300 focus:border-red-300': error,
                'border-neutral-300  focus:border-neutral-300': !error,
                'bg-neutral-100': disabled,
            })}>
                <DatePicker
                    onMonthChange={(month) => props.onMonthChange && props.onMonthChange(month.getMonth())}
                    onYearChange={(year) => props.onYearChange && props.onYearChange(year.getFullYear())}
                    highlightDates={[
                        {
                            'react-datepicker__day--highlighted-custom':
                                markedDates ? markedDates : []
                        }
                    ]}
                    minDate={type === 'date' ? new Date() : undefined}
                    includeTimes={includeTimes}
                    locale="it-custom"
                    selected={startDate}
                    onChange={(date) => { setStartDate(date); setOpen(false) }}
                    dateFormat={type === 'date' ? "dd/MM/yyyy" : 'HH:mm'}
                    showPopperArrow={false}
                    timeIntervals={timeIntervals}
                    placeholderText={type === 'date' ? 'gg/mm/aaaa' : 'hh:mm'}
                    wrapperClassName='w-full flex justify-center'
                    calendarClassName="bg-white"
                    calendarContainer={MyContainer}
                    calendarIconClassName='p-0'
                    showTimeSelect={type === 'time'}
                    showTimeSelectOnly={type === 'time'}
                    open={open}
                    disabled={disabled}
                    onInputClick={() => setOpen(true)}
                    onClickOutside={() => setOpen(false)}
                    showIcon
                    toggleCalendarOnIconClick
                    filterDate={(date) => {
                        if (!markedDates || markedDates.length === 0) return true;

                        return markedDates.some(d =>
                            d.getDate() === date.getDate() &&
                            d.getMonth() === date.getMonth() &&
                            d.getFullYear() === date.getFullYear()
                        );
                    }}
                    className="h-full cursor-pointer font-[Manrope] w-full border-0 outline-none p-0 text-text-sm"
                    renderDayContents={(day, date) => {
                        return <div className='h-6 w-6 rounded-full flex items-center justify-center m-0'>{day}</div>;
                    }}
                    icon={type === 'date' ?
                        <CalendarIcon color={colors.neutral[500]} size={"20"} onClick={(e) => disabled ? undefined : setOpen(open ? false : true)} /> :
                        <ClockIcon color={colors.neutral[500]} size={"20"} onClick={(e) => disabled ? undefined : setOpen(open ? false : true)} />
                    }
                    renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
                        <div className='bg-white flex items-center justify-between'>
                            <span className='ps-1 capitalize font-[Manrope] text-text-md font-semibold text-black'>{date.toLocaleString('it-IT', { month: 'long', year: 'numeric' })}</span>
                            <div className='flex gap-1'>
                                <button onClick={decreaseMonth}><ChevronIcon color={colors.neutral[500]} size={"24"} direction='l' /></button>
                                <button onClick={increaseMonth}><ChevronIcon color={colors.neutral[500]} size={"24"} direction='r' /></button>
                            </div>
                        </div>
                    )}
                    dayClassName={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);

                        return date < today
                            ? 'pointer-events-none opacity-50 cursor-not-allowed'
                            : date.getDay() === 0
                                ? 'text-red-500'
                                : '';
                    }}
                />
            </div>
            {supportingText && (
                <span
                    className={clsx('text-text-sm', {
                        'text-red-500': error,
                        'text-neutral-400': !error,
                    })}
                >
                    {supportingText}
                </span>
            )}
        </div>
    );
}

